import { SpotlightShell } from "@/components/marketplace/hero/spotlight-shell"
import { Price } from "@/components/marketplace/product/price"
import type { Product } from "@/components/marketplace/types"
import { ArrowIcon, Grid, Heading, LinkButton, Stack, Text } from "@/components/ui"
import { CoverLink } from "@/components/ui/cover-link"
import { TileImage } from "@/components/ui/tile-image"
import { Link } from "@/i18n/navigation"

interface CollectionSpotlightProps {
  eyebrow: string
  title: string
  sub?: string
  cta?: string
  ctaHref?: string
  image: string
  imageAlt?: string
  tiles: Product[]
}

export const CollectionSpotlight = ({ eyebrow, title, sub, cta, ctaHref, image, imageAlt, tiles }: CollectionSpotlightProps) => (
  <SpotlightShell>
    <div className="group press-banner bg-surface-newgray aspect-tile relative overflow-hidden @3xl:aspect-auto @3xl:min-h-110">
      <TileImage
        src={image}
        alt={imageAlt ?? ""}
        fill
        sizes="(max-width: 768px) 100vw, 50vw"
        className="duration-slow ease-productive object-cover transition-transform motion-safe:group-hover:scale-105"
      />
      <CoverLink href={cta ? ctaHref : undefined} />
    </div>
    <div className="@3xl:flex @3xl:flex-col @3xl:justify-center @3xl:p-700">
      <Stack gap="150" className="px-400 pt-400 pb-400 @3xl:px-0 @3xl:pt-0 @3xl:pb-0">
        <Text as="span" variant="eyebrow" tone="inherit" className="motion-safe:animate-rise-in bg-palette-bar text-palette-on-bar w-fit rounded-full px-300 py-150">
          {eyebrow}
        </Text>
        <Heading as="h2" variant="xl" family="display" tone="default" balance className="motion-safe:animate-rise-in animation-delay-100 @3xl:text-display-sm">
          {title}
        </Heading>
        {sub && (
          <Text as="p" variant="bodySm" family="text" tone="subtle" className="motion-safe:animate-rise-in animation-delay-200 @3xl:text-body mt-100">
            {sub}
          </Text>
        )}
        {cta && (
          <LinkButton
            href={ctaHref}
            size="sm"
            className="lift motion-safe:animate-rise-in animation-delay-300 mt-300 w-fit min-w-0 px-400 font-bold"
            iconRight={<ArrowIcon size={14} />}
          >
            {cta}
          </LinkButton>
        )}
      </Stack>
      <Grid cols={3} gap="200" className="px-350 pb-400 @3xl:mt-500 @3xl:px-0 @3xl:pb-0">
        {tiles.slice(0, 3).map((tile) => (
          <Stack
            key={tile.id}
            as={tile.href ? Link : "div"}
            {...(tile.href ? { href: tile.href } : {})}
            data-nelo-item={tile.id}
            data-nelo-serve={tile.serveId}
            gap="50"
            className="focus-visible:ring-brand rounded-8x group focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
          >
            <div className="rounded-8x bg-surface-newgray border-line-subtle group-hover:border-palette-bar duration-moderate ease-productive relative mb-150 aspect-square overflow-hidden border transition-colors">
              <TileImage
                src={tile.image}
                alt=""
                fill
                sizes="160px"
                className="duration-moderate ease-productive object-cover transition-transform motion-safe:group-hover:scale-103"
              />
            </div>
            {tile.brand && (
              <Text as="div" variant="caption" family="display" tone="subtle" className="tracking-wider uppercase">
                {tile.brand}
              </Text>
            )}
            <Text as="div" variant="captionLg" family="text" tone="default" className="line-clamp-2 font-medium">
              {tile.title}
            </Text>
            <Price size="sm" amount={tile.price} className="mt-50" />
          </Stack>
        ))}
      </Grid>
    </div>
  </SpotlightShell>
)
